/**
 * PrivacyPage Component
 *
 * Displays the privacy policy of the application.
 * Content is loaded from the API and rendered as Markdown.
 */

import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export function PrivacyPage() {
  const [content, setContent] = useState<string>('');
  const [updatedAt, setUpdatedAt] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/config/legal/privacy`);
        const data = await response.json();
        
        if (data.success) {
          setContent(data.data.content);
          setUpdatedAt(data.data.updatedAt);
        } else {
          setError('Impossible de charger la page');
        }
      } catch {
        setError('Erreur de connexion');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Format date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mx-auto mb-4"></div>
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mx-auto mb-8"></div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-8 space-y-4">
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-5/6"></div>
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-4">
            Politique de confidentialité
          </h1>
          <p className="text-red-500 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-2">
          Politique de confidentialité
        </h1>
        {updatedAt && (
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            Dernière mise à jour : {formatDate(updatedAt)}
          </p>
        )}
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-8">
        <div className="prose prose-slate dark:prose-invert max-w-none prose-headings:text-slate-900 dark:prose-headings:text-white prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-3 prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:leading-relaxed prose-ul:text-slate-600 dark:prose-ul:text-slate-300 prose-li:my-1 prose-strong:text-slate-700 dark:prose-strong:text-slate-200 prose-a:text-primary-600 dark:prose-a:text-primary-400 hover:prose-a:underline">
          <ReactMarkdown 
            remarkPlugins={[remarkBreaks]} 
            rehypePlugins={[rehypeRaw]}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPage;
